import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench, PoundSterling, ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { toast } from "sonner";

const STATUS_STYLE = {
  received: "bg-accent",
  "in progress": "bg-black text-white",
  ready: "bg-black text-white",
  completed: "bg-neutral-200",
  cancelled: "bg-white text-neutral-400",
};

function StatusPill({ s }) {
  return (
    <span className={`inline-block border-2 border-black px-3 py-1 font-display text-xs font-bold uppercase tracking-widest ${STATUS_STYLE[s] || "bg-white"}`} data-testid="status-pill">
      {s}
    </span>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState(null);
  const [sells, setSells] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/dashboard");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => setBookings([]));
    api.get("/sell-requests/mine").then((r) => setSells(r.data)).catch(() => setSells([]));
  }, [user]);

  if (loading || !user) {
    return <div className="max-w-3xl mx-auto px-4 py-24 font-display uppercase tracking-widest" data-testid="dashboard-loading">Loading…</div>;
  }

  const cancelBooking = async (b) => {
    if (!window.confirm(`Cancel booking ${b.reference}? This can't be undone.`)) return;
    try {
      await api.patch(`/bookings/${b._id}/cancel`);
      setBookings((bs) => bs.map((x) => (x._id === b._id ? { ...x, status: "cancelled" } : x)));
      toast.success("Booking cancelled");
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const payDeposit = async (b) => {
    try {
      const { data } = await api.post("/payments/checkout", { booking_id: b._id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16" data-testid="dashboard-page">
      <p className="eyebrow text-accent mb-4">My account</p>
      <h1 className="font-display font-black uppercase tracking-tighter text-5xl mb-2" data-testid="dashboard-heading">Hi, {user.name}</h1>
      <p className="text-neutral-500 mb-10">{user.email}</p>

      <div className="flex border-2 border-black mb-10 max-w-md">
        <button onClick={() => setTab("bookings")} data-testid="tab-bookings"
          className={`flex-1 py-3 font-display font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 ${tab === "bookings" ? "bg-black text-white" : "bg-white"}`}>
          <Wrench className="h-4 w-4" /> Repairs
        </button>
        <button onClick={() => setTab("sells")} data-testid="tab-sells"
          className={`flex-1 py-3 font-display font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 border-l-2 border-black ${tab === "sells" ? "bg-black text-white" : "bg-white"}`}>
          <PoundSterling className="h-4 w-4" /> Sell requests
        </button>
      </div>

      {tab === "bookings" && (
        <div data-testid="bookings-list">
          {!bookings ? (
            <p className="text-neutral-500">Loading…</p>
          ) : bookings.length === 0 ? (
            <div className="border-2 border-black p-10 text-center shadow-hard" data-testid="bookings-empty">
              <p className="text-neutral-600 mb-6">No repairs booked yet.</p>
              <Link to="/book" className="inline-flex items-center gap-2 border-2 border-black bg-accent px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover" data-testid="bookings-empty-cta">
                Book your first repair <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {bookings.map((b) => (
                <div key={b._id} className="border-2 border-black p-6 shadow-hard-sm flex flex-wrap items-center justify-between gap-4" data-testid={`booking-card-${b.reference}`}>
                  <div>
                    <p className="font-display font-bold uppercase tracking-wider">{b.brand} {b.model}</p>
                    <p className="text-sm text-neutral-500 mt-1">{b.issue} · {b.method === "dropoff" ? "Drop off" : "Post-in"} · {new Date(b.created_at).toLocaleDateString("en-GB")}</p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-display font-bold tracking-widest text-sm">{b.reference}</span>
                    <StatusPill s={b.status} />
                    {b.deposit_status === "paid" ? (
                      <span className="font-display text-xs font-bold uppercase tracking-widest text-accent" data-testid={`deposit-paid-${b.reference}`}>Deposit paid</span>
                    ) : b.status !== "cancelled" && (
                      <button onClick={() => payDeposit(b)} data-testid={`pay-deposit-${b.reference}`}
                        className="border-2 border-black bg-accent px-3 py-1 font-display text-xs font-bold uppercase tracking-widest shadow-hard-sm brutal-hover">
                        Pay £10 deposit
                      </button>
                    )}
                    {b.status === "received" && (
                      <button onClick={() => cancelBooking(b)} data-testid={`cancel-booking-${b.reference}`}
                        className="border-2 border-black bg-white px-3 py-1 font-display text-xs font-bold uppercase tracking-widest shadow-hard-sm brutal-hover">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "sells" && (
        <div data-testid="sells-list">
          {!sells ? (
            <p className="text-neutral-500">Loading…</p>
          ) : sells.length === 0 ? (
            <div className="border-2 border-black p-10 text-center shadow-hard" data-testid="sells-empty">
              <p className="text-neutral-600 mb-6">No sell requests yet.</p>
              <Link to="/sell" className="inline-flex items-center gap-2 border-2 border-black bg-accent px-6 py-3 font-display font-bold uppercase tracking-wider shadow-hard-sm brutal-hover" data-testid="sells-empty-cta">
                Sell a device <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {sells.map((s) => (
                <div key={s._id} className="border-2 border-black p-6 shadow-hard-sm flex flex-wrap items-center justify-between gap-4" data-testid={`sell-card-${s.reference}`}>
                  <div>
                    <p className="font-display font-bold uppercase tracking-wider">{s.brand} {s.model}</p>
                    <p className="text-sm text-neutral-500 mt-1">{s.condition} · {new Date(s.created_at).toLocaleDateString("en-GB")}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-display font-bold tracking-widest text-sm">{s.reference}</span>
                    <StatusPill s={s.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
